import React from 'react';
import { globals } from '../../../utils/globals';
import type { AutocompleteComponents } from '@algolia/autocomplete-js';
import { priceAndPointConverter, smallProductImageUrl } from '../../../utils';

export type ProductItemProps = {
  components: AutocompleteComponents;
  hit: any;
  isRecommended?: boolean;
};

const nonWineTypes = new Set(['Beer', 'Cider', 'Spirits & Liqueur', 'Food & Drink']);

export const ProductCard = ({ hit, components, isRecommended = false }: ProductItemProps): React.JSX.Element => {
  const renderCountryInfo = () => {
    const { productType, productWebType, country, grape } = hit;
    if (productType?.toLowerCase() === 'wine') {
      return [country, grape].filter(Boolean).join(' | ');
    }
    if (
      (productType === 'Case' && productWebType === 'Beer') ||
      (productType === 'NonWine' && nonWineTypes.has(productWebType))
    ) {
      return country || null;
    }
    return null;
  };

  const countryInfo = renderCountryInfo();

  // Price logic
  const [firstSku] = hit.skus;
  const { enPrimeurPrice, presellPayments, lowestPricePerBottle, buyersRRP, vppPrice, salePrice } = firstSku;

  const presellPrice = presellPayments?.[0]?.paymentAmount ?? vppPrice ?? lowestPricePerBottle;
  const enPrimeurDisplayPrice = enPrimeurPrice ?? vppPrice ?? lowestPricePerBottle;
  const defaultPrice = vppPrice ?? lowestPricePerBottle ?? salePrice;

  let displayPrice;
  let overrideFromText = false;
  let hideBuyersRRP = false;

  if (hit.productType === 'NonWine') {
    if (['Beer', 'Cider'].includes(hit.productWebType)) {
      displayPrice = lowestPricePerBottle;
      overrideFromText = true;
      hideBuyersRRP = true;
    } else if (salePrice) {
      displayPrice = salePrice;
      overrideFromText = true;
    }
  } else if (hit.preSell) {
    displayPrice = presellPrice;
    overrideFromText = true;
    hideBuyersRRP = true;
  } else if (hit.enPrimeur) {
    displayPrice = enPrimeurDisplayPrice;
    overrideFromText = true;
    hideBuyersRRP = true;
  } else if (hit.productType?.toLowerCase() === 'case' && salePrice) {
    displayPrice = salePrice;
    overrideFromText = true;
  } else {
    displayPrice = defaultPrice;
  }

  const isSundryProduct = hit.productType?.toLowerCase() === 'sundry';
  const showFromText =
    !overrideFromText &&
    !((hit.productType === 'Sundry' && hit.productWebType === 'Wine Accessories') || isSundryProduct);
  const showBuyersRRP = !hideBuyersRRP && buyersRRP && buyersRRP !== displayPrice;

  return (
    <div className={`aa-ItemWrapper ${isRecommended ? 'recommended-section' : 'product-section'}`}>
      <div className="aa-ItemContent">
        <div className={`aa-ItemIcon aa-ItemIcon--alignTop results-image`}>
          <img
            src={smallProductImageUrl(hit.itemCode)}
            alt={hit.productName}
            onError={e => {
              const img = e.currentTarget;
              img.onerror = null;
              img.src =
                hit.productType === 'Case'
                  ? '/next/images/default_images/case.png'
                  : '/next/images/default_images/bottle.png';
            }}
            loading="lazy"
          />
        </div>
        <div className="aa-ItemContentBody">
          <div className="aa-ItemContentTitle">
            {components.Highlight({
              hit,
              attribute: 'productName',
            })}{' '}
            {hit.vintage}
          </div>
          {globals?.algoliaNewView && (
            <>
              {countryInfo && (
                <div className="aa-ItemContentCountry" data-testid="country-info">
                  {countryInfo}
                </div>
              )}
              {typeof displayPrice === 'number' && !Number.isNaN(displayPrice) && (
                <div className="aa-ItemContentPrice" data-testid="price-block">
                  {showFromText && <span>from </span>}
                  {priceAndPointConverter(displayPrice)}{' '}
                  {showBuyersRRP && <span className="strike">{priceAndPointConverter(buyersRRP)}</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
